
// The ModelData format tag `fromWasmTrace` stamps, and the viewer checks.
export const FORMAT = "symtpu-pack/3";
// From the simulator's binary trace to the ModelData the viewer draws.
//
// `fromWasmTrace` reads a trace opened by the WebAssembly build of the simulator's own
// reader (symtpu crates/trace-wasm) and lays its events out as the growable typed
// columns the page transfers in one postMessage; `toRecords`/`decodeStrings` turn those
// columns into the per-core records the model reads. No DOM, no fetch: runs in the
// worker and under node alike.

const num = (v) => (v == null ? NaN : v);

// --- what crosses to the page, and what stays here ----------------------------------
//
// Every column of a binary pack is a view on the whole file, so the page holding one
// column holds all of it -- including the bundles' disassembly and the string table,
// which nothing but the chip page's listing and a bundle tooltip ever read, and which
// page does read into a buffer of their own and leaves the text behind: the worker
// renders a range of bundles from it on demand, or hands the whole of it over when a
// listing asks (disasm.js), and the file itself is let go either way.
//
// The copy is also where a column narrows, to the narrowest type that holds every
// value of it exactly: the exporter writes every number as a float64 and most of them
// are small integers, and the widths on disk are the widest any run could want -- a pc
// is four bytes there and two in most runs. A long trace, whose cycle counts and
// picoseconds do not fit, keeps its doubles, and the format on disk does not change.
// NaN, where a record reads a column that may be absent, becomes NONE, which is why
// such a column stops narrowing at Int32.
//
// A bundle's cycle count does not cross at all: it is its stamp over the stream's
// period, which is checked here, where both columns are.

export const NONE = -2147483648;
const wide = (v) => (v === NONE ? NaN : v);

// The per-core columns the page keeps, and the ones it does not.

// --- one column layout, two sources -------------------------------------------------
//
// A `/3` pack and the simulator's WebAssembly reader hand the page the same columns
// under the same names, with the same sentinels -- -1 for an absent index, NaN for an
// absent number, 0/1 for a flag, CSR offsets for what a row owns a run of -- so the
// records the model carries are made once, here, and only on the page: a million flag
// writes cross to it as fifty megabytes of arrays that are transferred rather than as
// a million objects that are copied.
//
// `data.cols` is the columns, `data.tables` the little string domains they index (a
// flag's core kind, a memory, a stall's reason, and `text` for everything else).

const nn = (v, bad = -1) => (v === bad ? null : v);            // -1 -> null
const nf = (v) => (Number.isNaN(v) || v === NONE ? null : v);  // NaN (narrowed or not) -> null
const HOST_EV = ["doorbell", "fw_cmd", "fw_done", "irq"];

// The records a source's columns stand for, in place. A source that already carries
// records (a JSONL build, an older pack) is left alone.
export function toRecords(data) {
  if (data.strBlob) { data.strings = decodeStrings(data.strBlob, data.strOff); data.strBlob = data.strOff = null; }
  if (!data.cols) return data;
  const c = data.cols, T = data.tables;
  const S = (id) => T.text[id] || "";
  data.waits = waitRecords(c.waits, T);
  data.flags = flagRecords(c.flags, T);
  data.stalls = stallRecords(c.stalls, T);
  data.dispatches = dispatchRecords(c.dispatches);
  data.dmas = dmaRecords(c.dmas, c.dma_runs, c.dma_credits, T);
  data.host = hostRecords(c.host, c.host_attrs, S);
  data.cols = null;
  return data;
}

// One UTF-8 blob and the offsets into it, as the array everything indexes. A listing is
// ASCII, and then the decoded text is as long as the blob and the byte offsets cut it
// directly -- one decode and a slice each, rather than a decode each.
export function decodeStrings(blob, off) {
  const n = off.length - 1, out = new Array(n);
  const dec = new TextDecoder();
  const all = dec.decode(blob);
  if (all.length === blob.length) {
    for (let i = 0; i < n; i++) out[i] = all.slice(off[i], off[i + 1]);
  } else {
    for (let i = 0; i < n; i++) out[i] = dec.decode(blob.subarray(off[i], off[i + 1]));
  }
  return out;
}

// A slice parks a quarter of a million times, and a panel or a tooltip ever looks at
// one: like a flag write below, a wait is its row number and the two things written
// onto it -- its class of idle (links.js) and, for a record that is not column-backed,
// nothing else. The fields are read off the columns through it, `open`, `t0`, `t1` and
// `stallPs` already in the shape the model used to normalise them into.
function waitRecords(c, T) {
  const FW = T.fw_core, n = c.chip.length, out = new Array(n);
  class Wait {
    constructor(i) { this.i = i; this.cls = null; }
    get chip() { return c.chip[this.i]; }
    get core() { return FW[c.core_kind[this.i]]; }
    get ci() { return nn(c.ci[this.i]); }
    get tile() { return nn(c.tile[this.i]); }
    get idx() { return c.idx[this.i]; }
    get v() { return wide(c.v[this.i]); }
    get v1() { return Number.isNaN(c.t1[this.i]) ? null : wide(c.v1[this.i]); }
    get cyc0() { return nf(c.cyc0[this.i]); }
    get cyc1() { return nf(c.cyc1[this.i]); }
    get stall() { return nf(c.stall[this.i]); }
    get stall_ps() { return nf(c.stall_ps[this.i]); }
    get open() { return Number.isNaN(c.t1[this.i]); }
    get t0() { const v = c.t0[this.i]; return Number.isNaN(v) ? 0 : v; }
    get t1() { return c.t1[this.i]; }
    get stallPs() { const v = c.stall_ps[this.i]; return Number.isNaN(v) ? 0 : v; }
    // The record it stands for, as the columns hold it: an absent stamp is null here,
    // where the fields above answer with what the run's readers want instead.
    toJSON() {
      const i = this.i;
      return {
        chip: this.chip, core: this.core, ci: this.ci, tile: this.tile, idx: this.idx, v: this.v,
        cyc0: this.cyc0, t0: nf(c.t0[i]), cyc1: this.cyc1, t1: nf(c.t1[i]), v1: this.v1,
        stall: this.stall, stall_ps: this.stall_ps,
      };
    }
  }
  for (let i = 0; i < n; i++) out[i] = new Wait(i);
  return out;
}

// A slice writes a million flags, and a panel or a tooltip ever looks at a handful. So
// a write's record is its row number and nothing else: the fields are read off the
// columns through it. Copying them out instead costs a hundred megabytes of short-lived
// objects and a third of a second of collecting them. `i`, which everything cites a
// write by, is the only field there is.
function flagRecords(c, T) {
  const FW = T.fw_core, n = c.chip.length, out = new Array(n);
  class FlagWrite {
    constructor(i) { this.i = i; }
    get at() { return c.at[this.i]; }
    get op() { return c.op[this.i] === 1 ? "incr" : "set"; }
    get chip() { return c.chip[this.i]; }
    get core() { return FW[c.core_kind[this.i]]; }
    get ci() { return nn(c.ci[this.i]); }
    get tile() { return nn(c.tile[this.i]); }
    get idx() { return c.idx[this.i]; }
    get old() { return wide(c.old[this.i]); }
    get new() { return wide(c.new_[this.i]); }
    get done() { return c.done[this.i] > 0; }
    get delta() { return c.op[this.i] === 1 ? wide(c.delta[this.i]) : undefined; }
    get cmd() { return c.cmd[this.i] >= 0 ? c.cmd[this.i] : undefined; }
    get dma() { return c.dma[this.i] >= 0 ? c.dma[this.i] : undefined; }
    get wall_ns() { return c.wall_ns ? c.wall_ns[this.i] : undefined; }
    // Serialised (the parity tests, a copy into a message) it is the record it stands
    // for; JSON drops the fields that do not apply, which is where they came from.
    toJSON() {
      const { op, chip, core, ci, tile, idx, at, old, done, delta, cmd, dma, wall_ns } = this;
      return { op, chip, core, ci, tile, idx, at, old, new: this.new, done, delta, cmd, dma, wall_ns };
    }
  }
  for (let i = 0; i < n; i++) out[i] = new FlagWrite(i);
  return out;
}

// `core` is the index of the stream that parked; a stall on anything else is dropped.
function stallRecords(c, T) {
  const R = T.stall_reason, FW = T.fw_core, out = [];
  for (let i = 0; i < c.core.length; i++) {
    if (c.core[i] < 0) continue;
    const reason = R[c.reason[i]];
    const x = {
      core: c.core[i], reason, step: c.step[i], pc: c.pc[i],
      cyc0: nf(c.cyc0[i]), t0: nf(c.t0[i]), cyc1: nf(c.cyc1[i]), t1: nf(c.t1[i]),
      stall: nf(c.stall[i]), stall_ps: nf(c.stall_ps[i]),
    };
    if (reason === "SyncDoneSpin") {
      x.v = c.v[i]; x.done = c.done[i] > 0; x.v1 = c.v1[i];
      x.flag_chip = nn(c.flag_chip[i]); x.flag_core = FW[c.flag_core_kind[i]];
      x.flag_ci = nn(c.flag_ci[i]); x.flag_tile = nn(c.flag_tile[i]); x.flag_idx = nn(c.flag_idx[i]);
    }
    if (c.round[i] >= 0) { x.round = c.round[i]; x.mask = c.mask[i]; }
    out.push(x);
  }
  return out;
}

function dispatchRecords(c) {
  const out = [];
  for (let i = 0; i < c.core.length; i++) {
    if (c.core[i] < 0) continue;
    const kind = c.kind[i] === 1 ? "launch" : "run";
    const d = {
      core: c.core[i], kind, run_id: nn(c.run_id[i]), cmd: nn(c.cmd[i]), pc0: nn(c.pc0[i]),
      cyc0: nf(c.cyc0[i]), t0: nf(c.t0[i]), idle: nf(c.idle[i]), idle_ps: nf(c.idle_ps[i]),
      step0: c.step0[i], cyc1: nf(c.cyc1[i]), t1: nf(c.t1[i]), pc1: nn(c.pc1[i]),
      bundles: c.bundles[i], nops: c.nops[i],
    };
    if (kind === "launch") { d.launch = nn(c.launch[i]); d.timem_gen = nn(c.timem_gen[i]); }
    out.push(d);
  }
  return out;
}

function dmaRecords(c, r, cr, T) {
  const MEM = T.memory, DC = T.dma_core, ISS = T.issuer, FW = T.fw_core;
  const end = (j, side) => ({
    chip: r[side + "_chip"][j], mem: MEM[r[side + "_mem"][j]], core: DC[r[side + "_core"][j]],
    ci: nn(r[side + "_ci"][j]), off: wide(r[side + "_off"][j]),
  });
  const n = c.id.length, out = new Array(n);
  for (let i = 0; i < n; i++) {
    const credits = [];
    for (let j = c.credit_off[i]; j < c.credit_off[i + 1]; j++) {
      credits.push({ chip: cr.chip[j], core: FW[cr.core_kind[j]], ci: nn(cr.ci[j]), tile: nn(cr.tile[j]), idx: cr.idx[j], delta: cr.delta[j], done: cr.set_done[j] > 0 });
    }
    // Which chips the descriptor touched, off the columns. A slice's descriptors have
    // half a million runs between them and the panel that opens one wants the shape of
    // them, so the runs themselves are built when something looks.
    const srcChips = new Set(), dstChips = new Set();
    let cross = false, ls = -1, ld = -1;          // a descriptor's runs repeat their ends
    for (let j = c.run_off[i]; j < c.run_off[i + 1]; j++) {
      const s = r.src_chip[j], d = r.dst_chip[j];
      if (s !== ls) srcChips.add(ls = s);
      if (d !== ld) dstChips.add(ld = d);
      if (s !== d) cross = true;
    }
    const iss = ISS[c.issuer[i]];
    const x = {
      id: c.id[i], chip: c.chip[i], by: c.issuer_index[i] < 0 ? iss : `${iss}(${c.issuer_index[i]})`,
      cmd: nn(c.cmd[i]), bytes: wide(c.bytes[i]), at: nf(c.at[i]), start: nf(c.start[i]),
      dur: nf(c.dur[i]), end: nf(c.end[i]), credits, srcChips, dstChips, cross,
    };
    const lo = c.run_off[i], hi = c.run_off[i + 1];
    Object.defineProperty(x, "runs", {
      configurable: true, enumerable: true,
      get() {
        const runs = [];
        for (let j = lo; j < hi; j++) runs.push({ src: end(j, "src"), dst: end(j, "dst"), len: wide(r.bytes[j]) });
        Object.defineProperty(x, "runs", { value: runs, writable: true, enumerable: true, configurable: true });
        return runs;
      },
    });
    out[i] = x;
  }
  return out;
}

// The host's own events, with a command's arguments flattened back into the record:
// `src.mem` and the like rebuild the exporter's nested objects.
function hostRecords(c, a, S) {
  const n = c.ev.length, out = new Array(n);
  for (let i = 0; i < n; i++) {
    const ev = HOST_EV[c.ev[i]], wall = c.wall_ns[i];
    // A trace's own wall clock is on every host event; a JSONL export's `t_ns` is the
    // packet's, and a pack keeps both.
    const tns = c.t_ns ? c.t_ns[i] : wall;
    const x = { ev, chip: c.chip[i] };
    if (!Number.isNaN(tns)) x.t_ns = tns;
    if (!Number.isNaN(wall)) x.wall_ns = wall;
    if (ev === "doorbell") { x.tail = c.tail[i]; x.prev = c.prev[i]; }
    else if (ev === "irq") { x.kind = S(c.kind[i]); x.n = nn(c.irq_n[i]); if (c.irq_core[i] >= 0) x.core = c.irq_core[i]; }
    else {
      x.id = c.id[i];
      if (!Number.isNaN(c.t[i])) x.t = c.t[i];
      if (ev === "fw_cmd") x.kind = S(c.kind[i]);
      else { x.status = c.ok[i] === 1 ? "ok" : "error"; if (c.err[i]) x.err = S(c.err[i]); }
      for (let j = c.attr_off[i]; j < c.attr_off[i + 1]; j++) {
        const key = S(a.key[j]), ty = a.type[j];
        const v = ty === 0 ? null : ty === 3 ? !!a.num[j] : ty === 4 ? S(a.str[j]) : ty === 5 ? tryJson(S(a.str[j])) : a.num[j];
        const dot = key.indexOf(".");
        if (dot > 0) { const p = key.slice(0, dot), q = key.slice(dot + 1); if (!x[p] || typeof x[p] !== "object") x[p] = {}; x[p][q] = v; }
        else x[key] = v;
      }
    }
    out[i] = x;
  }
  return out;
}

// --- the binary trace, through the simulator's reader compiled to WebAssembly -------
//
// Nothing is parsed: every record arrives as typed columns (symtpu crates/trace-wasm)
// and one string table, and this builds the same ModelData the pack path builds -- the
// same columns under the same names, so `toRecords` above serves both. The bundles'
// text is not here at all -- `units` gives each bundle's slot-unit mask for colouring,
// and the text is rendered on demand by the worker (see pack.worker.js).

// The listing line of a bundle from its slots, the way the exporter writes it: a
// TensorCore bundle is its slot texts three spaces apart; a SparseCore or tile bundle
// prefixes each with its slot name.
export function disOfSlots(kind, units, texts) {
  const parts = [];
  for (let i = 0; i < texts.length; i++) parts.push(kind === "tc" ? texts[i] : `${units[i]}: ${texts[i]}`);
  return parts.join("   ");
}

// Every getter on a wasm-bindgen table copies its column out of wasm memory, so a
// table is read once into a plain object and the copies are indexed from there.
function cols(table) {
  const out = {};
  for (const name of Object.getOwnPropertyNames(Object.getPrototypeOf(table))) {
    const d = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(table), name);
    if (d && d.get && name !== "is_empty") out[name] = table[name];
  }
  if (table.free) table.free();
  return out;
}

export function fromWasmTrace(trace, { gen = null, topology = null } = {}) {
  const t0 = performance.now();
  // Strings: one blob, decoded once; every text below is an index into `strings`.
  const blob = trace.strings(), offs = trace.string_offsets();
  const dec = new TextDecoder();
  const strings = new Array(offs.length - 1);
  for (let i = 0; i + 1 < offs.length; i++) strings[i] = dec.decode(blob.subarray(offs[i], offs[i + 1]));
  const S = (id) => strings[id] || "";
  const table = (ids) => Array.from(ids, S);
  const FW_CORE = table(trace.table_fw_core()), DMA_CORE = table(trace.table_dma_core()), MEMORY = table(trace.table_memory());
  const ISSUER = table(trace.table_issuer()), REASON = table(trace.table_stall_reason()), SKIND = table(trace.table_stream_kind());
  const UNITS = table(trace.unit_names());
  const h = trace.header();
  const meta = {
    gen: gen || ["v5e", "v5p", "v7x"][h.gen] || null, topology: topology ? Array.from(topology) : null,
    chips: 0, chips_seen: [], timing: true, events: Number(trace.event_count ? trace.event_count() : 0),
    last_seq: -1, truncated: h.truncated_bytes >= 0, t_end_ps: 0,
  };
  const chips = new Set();
  let tEnd = 0;
  const bump = (v) => { if (v > tEnd) tEnd = v; };

  // Streams and their issues.
  const st = cols(trace.streams());
  const streamKey = new Map();          // stream id -> core key
  const cores = [];
  const coreIndex = new Map();
  for (let i = 0; i < st.id.length; i++) {
    const kind = SKIND[st.kind[i]];
    if (kind !== "Tc" && kind !== "Scs" && kind !== "Tile") continue;
    const chip = st.chip[i], ci = st.core[i], tile = nn(st.tile[i]);
    chips.add(chip);
    const key = kind === "Tc" ? `c${chip}t${ci}` : `c${chip}s${ci}` + (kind === "Tile" ? `t${tile}` : "");
    streamKey.set(st.id[i], key);
    const is = trace.issues(st.id[i]);
    const len = is.len;
    if (!len && !is.nop_step.length) { if (is.free) is.free(); continue; }
    // The trace lists every issue, padding included; the pack lists the bundles that
    // did something and folds the padding into nop runs (a bundle with no populated
    // slot is padding, so `units` is 0 exactly there). A version-1 file cannot tell
    // the two apart, so it keeps every issue and no runs.
    const listed = h.has_listings;
    // Each getter copies its column out of wasm memory: read every column once.
    const units = is.units, rStep = is.step, rPc = is.pc, rCyc = is.cyc, rT = is.t, rGen = is.generation, rLd = is.last_delay;
    let n = 0;
    if (listed) { for (let k = 0; k < len; k++) if (units[k]) n++; } else n = len;
    const step = new Uint32Array(n), pc = new Uint32Array(n), cyc = new Float64Array(n), t = new Float64Array(n);
    const generation = new Uint32Array(n), lastDelay = new Uint8Array(n), umask = new Uint16Array(n), issueIdx = new Uint32Array(n);
    for (let k = 0, j = 0; k < len; k++) {
      if (listed && !units[k]) continue;
      step[j] = rStep[k]; pc[j] = rPc[k]; cyc[j] = rCyc[k]; t[j] = rT[k];
      generation[j] = rGen[k]; lastDelay[j] = rLd[k]; umask[j] = units[k]; issueIdx[j] = k;
      j++;
    }
    const core = {
      key, chip, kind: kind === "Tc" ? "tc" : kind === "Tile" ? "tile" : "sc", ci, tile,
      step, pc, cyc, t, dis: new Int32Array(0),
      slotOff: new Uint32Array(0), slotUnit: new Int32Array(0), slotIns: new Int32Array(0),
      nopStep: listed ? is.nop_step : new Uint32Array(0), nopPc: listed ? is.nop_pc : new Uint32Array(0), nopN: listed ? is.nop_n : new Uint32Array(0),
      regStep: new Uint32Array(0), regD: new Int32Array(0),
      units: [], umaskRaw: umask, unitNames: UNITS, generation, lastDelay, streamId: st.id[i], issueIdx, n,
    };
    if (n) bump(t[n - 1]);
    coreIndex.set(key, cores.length);
    cores.push(core);
    if (is.free) is.free();
  }

  // The event tables, as they are: the columns the pack writes, under the same names.
  // What has to be translated is the stream: the pack numbers a core, the trace an
  // id, and a stream that is not one of the cores above becomes -1 and is dropped.
  const coreOfStream = (ids) => Int32Array.from(ids, (id) => {
    const ci = coreIndex.get(streamKey.get(id));
    return ci === undefined ? -1 : ci;
  });
  const d = cols(trace.dispatches());
  d.core = coreOfStream(d.stream);
  // step0 / bundles / nops per dispatch from the cycle ranges: the pack counts them as
  // it reads the stream, and a trace has to be asked.
  d.step0 = new Uint32Array(d.core.length);
  d.bundles = new Uint32Array(d.core.length);
  d.nops = new Uint32Array(d.core.length);
  const byCore = new Map();
  for (let i = 0; i < d.core.length; i++) {
    if (d.core[i] < 0) continue;
    if (!byCore.has(d.core[i])) byCore.set(d.core[i], []);
    byCore.get(d.core[i]).push(i);
    if (!Number.isNaN(d.t1[i])) bump(d.t1[i]);
  }
  for (const [ci, ds] of byCore) {
    const c = cores[ci];
    ds.sort((a, b) => d.cyc0[a] - d.cyc0[b]);
    let i = 0;
    for (let k = 0; k < ds.length; k++) {
      const x = ds[k], hi = k + 1 < ds.length ? d.cyc0[ds[k + 1]] : Infinity;
      while (i < c.n && c.cyc[i] < d.cyc0[x]) i++;
      d.step0[x] = i;
      let count = 0;
      while (i < c.n && c.cyc[i] < hi) { i++; count++; }
      d.bundles[x] = count;
      const s0 = d.step0[x] < c.n ? c.step[d.step0[x]] : Infinity, s1 = i < c.n ? c.step[i] : Infinity;
      let nops = 0;
      for (let j = 0; j < c.nopStep.length; j++) { const sv = c.nopStep[j]; if (sv < s1 && (sv >= s0 || d.step0[x] === 0)) nops += c.nopN[j]; }
      d.nops[x] = nops;
    }
  }
  const w = cols(trace.waits());
  for (let i = 0; i < w.chip.length; i++) {
    chips.add(w.chip[i]);
    if (!Number.isNaN(w.t1[i])) bump(w.t1[i]);
  }
  const s = cols(trace.stalls());
  s.core = coreOfStream(s.stream);
  for (let i = 0; i < s.core.length; i++) if (!Number.isNaN(s.t1[i])) bump(s.t1[i]);
  const f = cols(trace.flags());
  for (let i = 0; i < f.chip.length; i++) bump(f.at[i]);
  const dm = cols(trace.dmas()), dr = cols(trace.dma_runs()), dc = cols(trace.dma_credits());
  for (let i = 0; i < dm.id.length; i++) {
    if (!Number.isNaN(dm.end[i])) bump(dm.end[i]);
    if (!Number.isNaN(dm.at[i])) bump(dm.at[i]);
  }
  const ho = cols(trace.host()), ha = cols(trace.host_attrs());
  let crossChip = 0;
  for (let i = 0; i < dm.id.length; i++) {
    for (let j = dm.run_off[i]; j < dm.run_off[i + 1]; j++) {
      if (dr.src_chip[j] !== dr.dst_chip[j]) { crossChip++; break; }
    }
  }
  let stallPs = 0, dispatched = 0, stalled = 0;
  for (let i = 0; i < w.chip.length; i++) if (!Number.isNaN(w.stall_ps[i])) stallPs += w.stall_ps[i];
  for (let i = 0; i < s.core.length; i++) {
    if (s.core[i] < 0) continue;
    stalled++;
    if (!Number.isNaN(s.stall_ps[i])) stallPs += s.stall_ps[i];
  }
  for (let i = 0; i < d.core.length; i++) if (d.core[i] >= 0) dispatched++;
  for (let i = 0; i < ho.ev.length; i++) {
    chips.add(ho.chip[i]);
    if (!Number.isNaN(ho.t[i])) bump(ho.t[i]);
  }

  meta.chips_seen = [...chips].sort((a, b) => a - b);
  meta.chips = topology ? topology[0] * topology[1] * topology[2] : chips.size;
  meta.t_end_ps = tEnd;
  const bundles = {};
  for (const c of cores) bundles[c.key] = c.n;
  const summary = {
    events: {}, bundles, dispatches: dispatched, dmas: dm.id.length, cross_chip_dmas: crossChip,
    flag_waits: w.chip.length, stall_ps: stallPs, other_stalls: stalled,
    build_ms: performance.now() - t0,
  };
  return {
    format: FORMAT, meta, strings, cores, summary, wasm: true,
    // `text` is the table the pack keeps for its own small strings; here every text is
    // already an id into the trace's own table, so that is the one.
    tables: { fw_core: FW_CORE, dma_core: DMA_CORE, memory: MEMORY, issuer: ISSUER, stall_reason: REASON, text: strings },
    cols: { waits: w, flags: f, stalls: s, dispatches: d, dmas: dm, dma_runs: dr, dma_credits: dc, host: ho, host_attrs: ha },
  };
}

function tryJson(s) { try { return JSON.parse(s); } catch (_) { return s; } }

// The typed arrays a ModelData carries, for postMessage's transfer list: the bundle
// columns of every core and every event column. A pack's columns are views over one
// buffer, so the set is what matters, not the count.
export function transferables(data) {
  const out = new Set();
  for (const c of data.cores) {
    for (const k of ["step", "pc", "cyc", "t", "dis", "slotOff", "slotUnit", "slotIns", "umask", "slotN",
                     "nopStep", "nopPc", "nopN", "regStep", "regD", "umaskRaw", "generation", "lastDelay", "issueIdx"]) if (c[k] && c[k].buffer) out.add(c[k].buffer);
  }
  for (const table in data.cols || {}) {
    for (const k in data.cols[table]) {
      const a = data.cols[table][k];
      if (a && a.buffer) out.add(a.buffer);
    }
  }
  for (const k of ["strBlob", "strOff"]) if (data[k] && data[k].buffer) out.add(data[k].buffer);
  return [...out];
}

// --- JSONL parsing ----------------------------------------------------------------

// Every parseable record in `text`, in order; a malformed line (the run was killed
// mid-write) is skipped, as pack.iter_events does.
